import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtDate } from "@/lib/credits";
import { FollowUpStatusBadge } from "@/pages/FollowUps";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Loader2, ClipboardCheck, Mail, Send, StickyNote, CalendarClock, Wrench } from "lucide-react";

const CARD = "rounded-2xl border border-white/[0.06] bg-[#141414] p-6";

const OUTCOMES = [
  { value: "customer_satisfied", label: "Customer Satisfied", help: "Marks this follow-up as completed." },
  { value: "left_voicemail", label: "Left Voicemail", help: "Stays open — you can schedule another reminder below." },
  { value: "no_answer", label: "No Answer", help: "Stays open — you can schedule another reminder below." },
  { value: "requested_reminder", label: "Requested Another Reminder", help: "Requires a new reminder date below." },
  { value: "needs_service", label: "Needs Service", help: "Requires notes. Stays visible and is flagged for the admin." },
  { value: "customer_declined", label: "Customer Declined Follow-Up", help: "Closes this follow-up." },
  { value: "unable_to_contact", label: "Unable to Contact", help: "Closes the follow-up, or schedule another reminder to keep it open." },
  { value: "other", label: "Other", help: "Requires notes." },
];
const REMINDER_REASONS = [
  "Customer requested a later call", "Waiting for customer response", "Service appointment follow-up",
  "Product-care check-in", "Additional information needed", "Other",
];
const wantsReminderFields = (o) => ["requested_reminder", "no_answer", "left_voicemail", "unable_to_contact"].includes(o);

function Info({ label, value }) {
  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-600">{label}</p>
      <p className="mt-1 text-sm text-neutral-200">{value || "—"}</p>
    </div>
  );
}

export default function FollowUpDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [fu, setFu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState("");
  const [busy, setBusy] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [email, setEmail] = useState({ subject: "", body: "" });
  const [reminders, setReminders] = useState([]);
  const [remOpen, setRemOpen] = useState(false);
  const [rem, setRem] = useState({ reminder_date: "", reminder_time: "", reason: "", notes: "" });
  const [outOpen, setOutOpen] = useState(false);
  const [out, setOut] = useState({ outcome: "", notes: "", reminder_date: "", reminder_time: "", reason: "" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/follow-ups/${id}`);
      setFu(data);
      api.get(`/follow-ups/${id}/reminders`).then(({ data }) => setReminders(data)).catch(() => {});
    } catch (e) {
      toast.error("Follow-up not found");
      navigate("/follow-ups");
    } finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const refreshReminders = async () => {
    try { const { data } = await api.get(`/follow-ups/${id}/reminders`); setReminders(data); } catch (e) {}
  };

  const saveReminder = async () => {
    if (!rem.reminder_date) { toast.error("Reminder date is required"); return; }
    if (!rem.reason.trim()) { toast.error("A reason is required"); return; }
    setBusy(true);
    try {
      await api.post(`/follow-ups/${id}/reminders`, rem);
      toast.success("Reminder scheduled");
      setRemOpen(false);
      setRem({ reminder_date: "", reminder_time: "", reason: "", notes: "" });
      refreshReminders();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to schedule"); }
    finally { setBusy(false); }
  };

  const completeReminder = async (rid) => {
    try {
      await api.post(`/reminders/${rid}/complete`);
      setReminders((prev) => prev.map((r) => (r.id === rid ? { ...r, status: "completed", display_status: "completed" } : r)));
      toast.success("Reminder completed");
    } catch (e) { toast.error("Failed"); }
  };

  const cancelReminder = async (rid) => {
    try {
      await api.post(`/reminders/${rid}/cancel`);
      setReminders((prev) => prev.map((r) => (r.id === rid ? { ...r, status: "cancelled", display_status: "cancelled" } : r)));
      toast.success("Reminder cancelled");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const recordOutcome = async () => {
    if (!out.outcome) { toast.error("Please select an outcome"); return; }
    if (["needs_service", "other"].includes(out.outcome) && !out.notes.trim()) { toast.error("Notes are required for this outcome"); return; }
    if (out.outcome === "requested_reminder" && !out.reminder_date) { toast.error("A reminder date is required"); return; }
    setBusy(true);
    try {
      const payload = { outcome: out.outcome, notes: out.notes };
      if (wantsReminderFields(out.outcome) && out.reminder_date) {
        payload.reminder_date = out.reminder_date;
        payload.reminder_time = out.reminder_time;
        payload.reason = out.reason;
      }
      await api.post(`/follow-ups/${id}/outcome`, payload);
      toast.success("Outcome recorded");
      setOutOpen(false);
      setOut({ outcome: "", notes: "", reminder_date: "", reminder_time: "", reason: "" });
      load();
      refreshReminders();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to record outcome"); }
    finally { setBusy(false); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/follow-ups/${id}/notes`, { note: noteText.trim() });
      setFu((prev) => ({ ...prev, notes: [data, ...(prev.notes || [])] }));
      setNoteText("");
      toast.success("Note added");
    } catch (e) { toast.error("Failed to add note"); }
    finally { setBusy(false); }
  };

  const openEmail = () => {
    const biz = user?.business_name || "your installer";
    setEmail({
      subject: "Checking In After Your SGBINOXE Installation",
      body: `Hello ${fu.customer_name},\n\nWe hope you're enjoying your SGBINOXE installation.\n\nIt has been approximately 30 days since your installation, and we wanted to check in to ensure everything is meeting your expectations.\n\nIf you have any questions or need assistance, please don't hesitate to contact us.\n\nThank you for choosing ${biz}.`,
    });
    setEmailOpen(true);
  };

  const sendEmail = async () => {
    if (!email.subject.trim() || !email.body.trim()) { toast.error("Subject and message are required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/follow-ups/${id}/email-customer`, email);
      toast.success(data.message || "Email sent");
      setEmailOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to send email"); }
    finally { setBusy(false); }
  };

  if (loading || !fu) {
    return <DashboardLayout title="Follow-Up"><div className="flex items-center justify-center py-24 text-neutral-500"><Loader2 className="h-5 w-5 animate-spin" /></div></DashboardLayout>;
  }

  const isAdmin = user?.role === "super_admin";
  const remStatusColor = (s) => ({ completed: "text-emerald-400", cancelled: "text-neutral-500", overdue: "text-[#FF6069]", due: "text-amber-400" }[s] || "text-amber-400");
  const outMeta = OUTCOMES.find((o) => o.value === out.outcome);

  return (
    <DashboardLayout title="Follow-Up Details">
      <button onClick={() => navigate("/follow-ups")} className="mb-5 inline-flex items-center gap-2 text-sm text-neutral-400 hover:text-white" data-testid="fu-back-btn"><ArrowLeft className="h-4 w-4" /> Back to follow-ups</button>

      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="font-display text-2xl font-semibold tracking-tight text-white" data-testid="fu-customer-name">{fu.customer_name}</h2>
            <FollowUpStatusBadge status={fu.follow_up_status} pastDue={fu.past_due} />
          </div>
          <p className="mt-1 text-sm text-neutral-500">{fu.certificate_number} · Follow-up due {fmtDate(fu.follow_up_date)}{fu.follow_up_status === "pending" && fu.days_overdue > 0 ? ` · ${fu.days_overdue} days overdue` : ""}</p>
          {fu.last_outcome_label && <p className="mt-1 text-xs text-neutral-600">Last outcome: <span className="text-neutral-400">{fu.last_outcome_label}</span> · {fmtDate(fu.last_outcome_at, true)}</p>}
        </div>
        {!isAdmin && (
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setOutOpen(true)} className="inline-flex items-center gap-2 rounded-lg bg-[#E30613] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#FF1C2A]" data-testid="record-outcome-btn"><ClipboardCheck className="h-4 w-4" /> Record Outcome</button>
            <button onClick={() => setRemOpen(true)} className="inline-flex items-center gap-2 rounded-lg border border-white/[0.1] px-4 py-2.5 text-sm font-medium text-neutral-200 hover:bg-white/[0.05]" data-testid="schedule-reminder-btn"><CalendarClock className="h-4 w-4" /> Schedule Reminder</button>
            <button onClick={openEmail} className="inline-flex items-center gap-2 rounded-lg border border-white/[0.1] px-4 py-2.5 text-sm font-medium text-neutral-200 hover:bg-white/[0.05]" data-testid="fu-email-customer-btn"><Mail className="h-4 w-4" /> Email Customer</button>
          </div>
        )}
      </div>

      {fu.follow_up_status === "needs_service" && (
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-orange-500/30 bg-orange-500/10 px-5 py-4" data-testid="needs-service-banner">
          <Wrench className="mt-0.5 h-5 w-5 text-orange-400" />
          <div>
            <p className="text-sm font-semibold text-orange-300">Flagged: Needs Service</p>
            <p className="mt-1 text-xs text-neutral-400">This customer requires additional service. It stays visible until resolved and is reported to SGBINOXE.</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className={CARD}>
          <h3 className="mb-4 font-display text-base font-medium text-white">Customer</h3>
          <div className="space-y-4">
            <Info label="Name" value={fu.customer_name} />
            <Info label="Email" value={fu.customer_email} />
            <Info label="Phone" value={fu.customer_phone} />
          </div>
        </div>
        <div className={CARD}>
          <h3 className="mb-4 font-display text-base font-medium text-white">Vehicle</h3>
          <div className="space-y-4">
            <Info label="Vehicle" value={`${fu.vehicle_year} ${fu.vehicle_make} ${fu.vehicle_model}`} />
            <Info label="VIN" value={fu.vin} />
            <Info label="Color / Plate" value={[fu.vehicle_color, fu.license_plate].filter(Boolean).join(" · ")} />
          </div>
        </div>
        <div className={CARD}>
          <h3 className="mb-4 font-display text-base font-medium text-white">Installation</h3>
          <div className="space-y-4">
            <Info label="Product" value={fu.product_installed} />
            <Info label="Package" value={fu.installation_package || "Not Specified"} />
            <Info label="Installed On" value={fmtDate(fu.installation_date)} />
            <Info label="Installer" value={fu.installer_business} />
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-2">
        <div className={CARD}>
          <h3 className="mb-3 font-display text-base font-medium text-white">Installer Notes</h3>
          <p className="whitespace-pre-wrap text-sm text-neutral-300">{fu.installer_notes || "No installation notes."}</p>
        </div>
        <div className={CARD}>
          <h3 className="mb-4 font-display text-base font-medium text-white">Follow-Up Notes</h3>
          {!isAdmin && (
            <div className="mb-5 flex items-start gap-3">
              <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={2} placeholder="Log a call, message or outcome…" className="rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white" data-testid="fu-note-input" />
              <button onClick={addNote} disabled={busy || !noteText.trim()} className="inline-flex shrink-0 items-center gap-2 self-start rounded-lg bg-[#E30613] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#FF1C2A] disabled:opacity-60" data-testid="fu-add-note-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <StickyNote className="h-4 w-4" />} Add</button>
            </div>
          )}
          {(!fu.notes || fu.notes.length === 0) ? <p className="py-6 text-center text-sm text-neutral-500">No follow-up notes yet.</p> : (
            <div className="space-y-3">
              {fu.notes.map((n) => (
                <div key={n.id} className="rounded-xl border border-white/[0.06] bg-[#0A0A0A] p-4" data-testid="fu-note-item">
                  <p className="text-sm text-neutral-200">{n.note}</p>
                  <p className="mt-2 text-xs text-neutral-600">{n.author_name} · {fmtDate(n.created_at, true)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-6">
        <div className={CARD}>
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-display text-base font-medium text-white">Custom Reminders</h3>
            {!isAdmin && (
              <button onClick={() => setRemOpen(true)} className="inline-flex items-center gap-2 rounded-lg border border-white/[0.1] px-3 py-2 text-sm font-medium text-neutral-200 hover:bg-white/[0.05]" data-testid="schedule-reminder-btn-2"><CalendarClock className="h-4 w-4" /> Schedule Another Reminder</button>
            )}
          </div>
          {reminders.length === 0 ? <p className="py-6 text-center text-sm text-neutral-500">No custom reminders scheduled.</p> : (
            <div className="space-y-3">
              {reminders.map((r) => (
                <div key={r.id} className="flex items-start justify-between gap-4 rounded-xl border border-white/[0.06] bg-[#0A0A0A] p-4" data-testid="reminder-item">
                  <div>
                    <p className="text-sm text-white">{r.reason || "Reminder"} · <span className="text-neutral-400">{fmtDate(r.reminder_date)}{r.reminder_time ? ` ${r.reminder_time}` : ""}</span></p>
                    {r.notes && <p className="mt-1 text-xs text-neutral-500">{r.notes}</p>}
                    <p className="mt-1 text-xs text-neutral-600">{r.created_by_name} · <span className={remStatusColor(r.display_status || r.status)}>{r.display_status || r.status}</span></p>
                  </div>
                  {!isAdmin && !["completed", "cancelled"].includes(r.status) && (
                    <div className="flex shrink-0 gap-2">
                      <button onClick={() => completeReminder(r.id)} className="rounded-lg border border-white/[0.1] px-3 py-1.5 text-xs text-neutral-300 hover:bg-white/[0.05]" data-testid={`complete-reminder-${r.id}`}>Complete</button>
                      <button onClick={() => cancelReminder(r.id)} className="rounded-lg border border-white/[0.1] px-3 py-1.5 text-xs text-neutral-400 hover:bg-white/[0.05]" data-testid={`cancel-reminder-${r.id}`}>Cancel</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Record Outcome modal */}
      <Dialog open={outOpen} onOpenChange={setOutOpen}>
        <DialogContent className="border-white/[0.08] bg-[#141414] text-white" data-testid="outcome-modal">
          <DialogHeader><DialogTitle>Record Follow-Up Outcome</DialogTitle><DialogDescription className="text-neutral-500">Select what happened when you contacted this customer.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-neutral-300">Outcome *</Label>
              <Select value={out.outcome} onValueChange={(v) => setOut((s) => ({ ...s, outcome: v }))}>
                <SelectTrigger className="h-11 rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white" data-testid="outcome-select"><SelectValue placeholder="Select an outcome" /></SelectTrigger>
                <SelectContent className="border-white/[0.08] bg-[#141414] text-white">
                  {OUTCOMES.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`outcome-option-${o.value}`}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
              {outMeta && <p className="text-xs text-neutral-500">{outMeta.help}</p>}
            </div>
            {wantsReminderFields(out.outcome) && (
              <div className="rounded-xl border border-white/[0.06] bg-[#0A0A0A] p-4 space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Schedule another reminder{out.outcome === "requested_reminder" ? " *" : " (optional)"}</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label className="text-neutral-400 text-xs">Date</Label><Input type="date" value={out.reminder_date} onChange={(e) => setOut((s) => ({ ...s, reminder_date: e.target.value }))} className="h-10 rounded-lg border-white/[0.1] bg-[#141414] text-white [color-scheme:dark]" data-testid="outcome-reminder-date" /></div>
                  <div className="space-y-1.5"><Label className="text-neutral-400 text-xs">Time</Label><Input type="time" value={out.reminder_time} onChange={(e) => setOut((s) => ({ ...s, reminder_time: e.target.value }))} className="h-10 rounded-lg border-white/[0.1] bg-[#141414] text-white [color-scheme:dark]" data-testid="outcome-reminder-time" /></div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-neutral-400 text-xs">Reason</Label>
                  <Select value={out.reason} onValueChange={(v) => setOut((s) => ({ ...s, reason: v }))}>
                    <SelectTrigger className="h-10 rounded-lg border-white/[0.1] bg-[#141414] text-white" data-testid="outcome-reminder-reason"><SelectValue placeholder="Select a reason" /></SelectTrigger>
                    <SelectContent className="border-white/[0.08] bg-[#141414] text-white">{REMINDER_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-neutral-300">Notes {["needs_service", "other"].includes(out.outcome) ? "*" : ""}</Label>
              <Textarea value={out.notes} onChange={(e) => setOut((s) => ({ ...s, notes: e.target.value }))} rows={3} className="rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white" data-testid="outcome-notes" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOutOpen(false)} className="rounded-lg border border-white/[0.1] px-4 py-2 text-sm text-neutral-300 hover:bg-white/[0.05]">Cancel</button>
            <button onClick={recordOutcome} disabled={busy} className="inline-flex items-center gap-2 rounded-lg bg-[#E30613] px-5 py-2 text-sm font-medium text-white hover:bg-[#FF1C2A] disabled:opacity-60" data-testid="save-outcome-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Record Outcome"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule reminder modal */}
      <Dialog open={remOpen} onOpenChange={setRemOpen}>
        <DialogContent className="border-white/[0.08] bg-[#141414] text-white" data-testid="reminder-modal">
          <DialogHeader><DialogTitle>Schedule Reminder</DialogTitle><DialogDescription className="text-neutral-500">Create an additional reminder for this installation.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label className="text-neutral-300">Reminder Date *</Label><Input type="date" value={rem.reminder_date} onChange={(e) => setRem((s) => ({ ...s, reminder_date: e.target.value }))} className="h-11 rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white [color-scheme:dark]" data-testid="reminder-date" /></div>
              <div className="space-y-2"><Label className="text-neutral-300">Time (optional)</Label><Input type="time" value={rem.reminder_time} onChange={(e) => setRem((s) => ({ ...s, reminder_time: e.target.value }))} className="h-11 rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white [color-scheme:dark]" data-testid="reminder-time" /></div>
            </div>
            <div className="space-y-2">
              <Label className="text-neutral-300">Reason *</Label>
              <Select value={rem.reason} onValueChange={(v) => setRem((s) => ({ ...s, reason: v }))}>
                <SelectTrigger className="h-11 rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white" data-testid="reminder-reason"><SelectValue placeholder="Select a reason" /></SelectTrigger>
                <SelectContent className="border-white/[0.08] bg-[#141414] text-white">{REMINDER_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label className="text-neutral-300">Notes</Label><Textarea value={rem.notes} onChange={(e) => setRem((s) => ({ ...s, notes: e.target.value }))} rows={3} className="rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white" data-testid="reminder-notes" /></div>
          </div>
          <DialogFooter>
            <button onClick={() => setRemOpen(false)} className="rounded-lg border border-white/[0.1] px-4 py-2 text-sm text-neutral-300 hover:bg-white/[0.05]">Cancel</button>
            <button onClick={saveReminder} disabled={busy} className="inline-flex items-center gap-2 rounded-lg bg-[#E30613] px-5 py-2 text-sm font-medium text-white hover:bg-[#FF1C2A] disabled:opacity-60" data-testid="save-reminder-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Schedule"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email customer modal */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="border-white/[0.08] bg-[#141414] text-white" data-testid="fu-email-modal">
          <DialogHeader><DialogTitle>Email {fu.customer_name}</DialogTitle><DialogDescription className="text-neutral-500">Send a check-in email to your customer. You can edit it before sending.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-neutral-300">Subject</Label>
              <Input value={email.subject} onChange={(e) => setEmail((s) => ({ ...s, subject: e.target.value }))} className="h-11 rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white" data-testid="fu-email-subject" />
            </div>
            <div className="space-y-2">
              <Label className="text-neutral-300">Message</Label>
              <Textarea value={email.body} onChange={(e) => setEmail((s) => ({ ...s, body: e.target.value }))} rows={9} className="rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white" data-testid="fu-email-body" />
            </div>
            <p className="text-xs text-neutral-500">Your business contact details are automatically added to the footer so the customer can reach you directly.</p>
          </div>
          <DialogFooter>
            <button onClick={() => setEmailOpen(false)} className="rounded-lg border border-white/[0.1] px-4 py-2 text-sm text-neutral-300 hover:bg-white/[0.05]">Cancel</button>
            <button onClick={sendEmail} disabled={busy} className="inline-flex items-center gap-2 rounded-lg bg-[#E30613] px-5 py-2 text-sm font-medium text-white hover:bg-[#FF1C2A] disabled:opacity-60" data-testid="fu-send-email-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send Email</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
